import { call, put, takeEvery } from 'redux-saga/effects';
import { partsApi, Part } from '../../services/partsApi';
import {
  fetchPartsStart,
  fetchPartsSuccess,
  fetchPartsFailure,
  addPart,
  updatePart,
  deletePart,
} from '../reducers/partsSlice';
import { PayloadAction } from '@reduxjs/toolkit';

// Fetch parts saga
function* fetchPartsApi(action: PayloadAction<{ page?: number; limit?: number; search?: string } | undefined>): Generator<any, void, any> {
  try {
    console.log('Parts Saga: Starting API call with params:', action.payload);
    
    const response = yield call(partsApi.getAll, action.payload || {});
    console.log('Parts Saga: Raw API response:', response);
    console.log('Parts Saga: Response type:', typeof response);
    console.log('Parts Saga: Response keys:', response ? Object.keys(response) : 'null/undefined');
    console.log('Parts Saga: Response.data exists:', !!response.data);
    console.log('Parts Saga: Response.data type:', typeof response.data);
    console.log('Parts Saga: Response.data is array:', Array.isArray(response.data));
    console.log('Parts Saga: Response.data length:', response.data?.length);
    
    // Parse the response body if it's a string
    let parsedResponse;
    if (response.body && typeof response.body === 'string') {
      try {
        parsedResponse = JSON.parse(response.body);
        console.log('Parts Saga: Parsed response body:', parsedResponse);
        console.log('Parts Saga: Parsed data type:', typeof parsedResponse.data);
        console.log('Parts Saga: Parsed data is array:', Array.isArray(parsedResponse.data));
        console.log('Parts Saga: Parsed data length:', parsedResponse.data?.length);
      } catch (error) {
        console.error('Parts Saga: Failed to parse response body:', error);
        parsedResponse = response;
      }
    } else {
      parsedResponse = response;
    }
    
    // Extract the data and create the proper payload structure
    const partsData = parsedResponse.data || parsedResponse;
    console.log('Parts Saga: Extracted partsData:', partsData);
    console.log('Parts Saga: partsData type:', typeof partsData);
    console.log('Parts Saga: partsData is array:', Array.isArray(partsData));
    
    // Handle case where Lambda returns raw array vs wrapped response
    let actualPartsArray;
    if (Array.isArray(partsData)) {
      // Lambda returned raw array
      actualPartsArray = partsData;
      console.log('Parts Saga: Using partsData as raw array');
    } else if (parsedResponse.data && Array.isArray(parsedResponse.data)) {
      // Lambda returned wrapped response
      actualPartsArray = parsedResponse.data;
      console.log('Parts Saga: Using parsedResponse.data as wrapped array');
    } else {
      // Fallback - try to find any array in the response
      actualPartsArray = [];
      console.warn('Parts Saga: No array found in response, using empty array');
    }
    
    console.log('Parts Saga: Actual parts array:', actualPartsArray);
    console.log('Parts Saga: Array length:', actualPartsArray.length);
    console.log('Parts Saga: First array item:', actualPartsArray[0]);
    
    // Transform PascalCase fields to camelCase that frontend expects
    const transformedData = actualPartsArray.map((item: any) => ({
      id: item.Id,
      part_num: item.Number,
      description: item.Description,
      client_id: item.ClientId,
      part_issue: item.Issue,
      price_per_unit: item.PricePerUnit,
      created_by: item.CreatedBy,
      modified_by: item.ModifiedBy,
      part_status_id: item.PartStatusId,
      part_classification_id: item.Classification,
      approved_by: item.ApprovedBy,
      created_date: item.CreatedDate,
      modified_date: item.ModifiedDate,
      obsolete_date: item.ObsoleteDate || null,
      is_active: item.Active,
      // Transform nested objects
      status: item.Status ? {
        status_id: item.Status.Id,
        status_name: item.Status.StatusName,
        status_code: item.Status.StatusCode,
        description: item.Status.Description,
        created_date: item.Status.CreatedDate,
        modified_date: item.Status.ModifiedDate,
        obsolete_date: item.Status.ObsoleteDate || null,
        is_active: item.Status.Active
      } : null,
      client: item.Client ? {
        client_id: item.Client.Id,
        name: item.Client.Name,
        description: item.Client.Description,
        created_date: item.Client.CreatedDate,
        modified_date: item.Client.ModifiedDate,
        obsolete_date: item.Client.ObsoleteDate || null,
        is_active: item.Client.Active
      } : null,
      creator: item.Creator ? {
        user_id: item.Creator.Id,
        role_id: item.Creator.RoleId,
        first_name: item.Creator.FirstName,
        last_name: item.Creator.LastName,
        employee_number: item.Creator.EmployeeNumber,
        email: item.Creator.Email,
        is_supervisor: item.Creator.IsSupervisor,
        department_id: item.Creator.DepartmentId,
        user_image: item.Creator.UserImage || null,
        created_date: item.Creator.CreatedDate,
        modified_date: item.Creator.ModifiedDate,
        obsolete_date: item.Creator.ObsoleteDate || null,
        is_active: item.Creator.Active
      } : null
    }));
    
    console.log('Parts Saga: Transformed data:', transformedData);
    console.log('Parts Saga: First transformed item:', transformedData[0]);
    
    const payload = {
      data: transformedData,
      total: response.total || transformedData.length,
      page: response.page || action.payload?.page || 1,
      limit: response.limit || action.payload?.limit || 10
    };
    
    console.log('Parts Saga: Final payload being sent to reducer:', payload);
    
    yield put(fetchPartsSuccess(payload));
  } catch (error: any) {
    console.error('Parts Saga: Error occurred:', error);
    yield put(fetchPartsFailure(error.message || 'Failed to fetch parts'));
  }
}

// Create part saga
function* createPartApi(action: PayloadAction<Omit<Part, 'id'>>): Generator<any, void, any> {
  try {
    const response = yield call(partsApi.create, action.payload);
    // Refresh the parts list after creating
    yield put(fetchPartsStart({}));
    return response;
  } catch (error: any) {
    yield put(fetchPartsFailure(error.message || 'Failed to create part'));
  }
}

// Update part saga
function* updatePartApi(action: PayloadAction<Part>): Generator<any, void, any> {
  try {
    const { id, ...partData } = action.payload;
    const response = yield call(partsApi.update, id!, partData);
    // Refresh the parts list after updating
    yield put(fetchPartsStart({}));
    return response;
  } catch (error: any) {
    yield put(fetchPartsFailure(error.message || 'Failed to update part'));
  }
}

// Delete part saga
function* deletePartApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(partsApi.delete, action.payload);
    // Refresh the parts list after deleting
    yield put(fetchPartsStart({}));
  } catch (error: any) {
    yield put(fetchPartsFailure(error.message || 'Failed to delete part'));
  }
}

// Root parts saga
export default function* partsSaga(): Generator<any, void, any> {
  yield takeEvery(fetchPartsStart.type, fetchPartsApi);
  yield takeEvery(addPart.type, createPartApi);
  yield takeEvery(updatePart.type, updatePartApi);
  yield takeEvery(deletePart.type, deletePartApi);
} 